import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import { PlayArrow as PlayArrowIcon } from '@mui/icons-material';
import AiStoriesChannelService from '../services/AiStoriesChannelService';
import { formatEastern } from '../utils/easternTime';

export const AUTOMATION_STATE_SEVERITY = {
  ok: 'success',
  waiting: 'warning',
  blocked: 'error',
  idle: 'info',
};

const STAGES = {
  script: { label: 'Writing script', color: 'default' },
  voiceover: { label: 'Voiceover', color: 'default' },
  rendering: { label: 'Rendering', color: 'info' },
  uploading: { label: 'Uploading', color: 'info' },
  scheduled: { label: 'Scheduled on YouTube', color: 'success' },
  upload_attention: { label: 'Upload needs attention', color: 'warning' },
  failed: { label: 'Failed', color: 'error' },
};

export default function ChannelAutomationDialog({ channel, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const refresh = useCallback(async () => {
    try {
      setData(await AiStoriesChannelService.getAutomation(channel.id));
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load the schedule.');
    } finally {
      setLoading(false);
    }
  }, [channel.id]);

  useEffect(() => {
    refresh();
    // Stages advance in the background while the dialog is open.
    const timer = setInterval(refresh, 15000);
    return () => clearInterval(timer);
  }, [refresh]);

  const runNow = async () => {
    try {
      setRunning(true);
      setNotice('');
      const result = await AiStoriesChannelService.runAutomation(channel.id);
      setData(result);
      setNotice(
        result.created
          ? `Scheduled ${result.created} new video${result.created === 1 ? '' : 's'}.`
          : 'No new videos were needed.',
      );
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not run the scheduler.');
    } finally {
      setRunning(false);
    }
  };

  const automation = data?.automation;
  const upcoming = data?.upcoming || [];

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Automation schedule · {channel.name}</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={2}>
            {error && <Alert severity="error">{error}</Alert>}
            {notice && <Alert severity="success" onClose={() => setNotice('')}>{notice}</Alert>}
            {automation && (
              <>
                {automation.message && (
                  <Alert severity={AUTOMATION_STATE_SEVERITY[automation.state] || 'info'}>
                    {automation.message}
                  </Alert>
                )}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <Chip
                    label={automation.enabled ? `Automated · ${automation.videosPerDay}/day` : 'Manual'}
                    color={automation.enabled ? 'primary' : 'default'}
                  />
                  {automation.niche && <Chip variant="outlined" label={`Niche: ${automation.niche}`} />}
                  {automation.backlogCount !== null && (
                    <Chip
                      variant="outlined"
                      color={automation.backlogCount ? 'default' : 'warning'}
                      label={`${automation.backlogCount} idea${automation.backlogCount === 1 ? '' : 's'} in backlog`}
                    />
                  )}
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Every hour, the scheduler fills today&apos;s and tomorrow&apos;s publish slots from the
                  idea backlog. Each video is published at a random time in its part of the 9 AM–9 PM
                  Eastern window.
                  {automation.lastRunAt && ` Last run: ${formatEastern(automation.lastRunAt)}.`}
                </Typography>
              </>
            )}
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Upcoming videos</Typography>
            {!upcoming.length && (
              <Typography variant="body2" color="text.secondary">
                Nothing is scheduled yet.
              </Typography>
            )}
            {upcoming.map((video) => {
              const stage = STAGES[video.stage] || STAGES.script;
              return (
                <Box
                  key={video.storyId}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '170px minmax(0, 1fr) auto' },
                    alignItems: 'center',
                    gap: { xs: 0.5, sm: 2 },
                    py: 1,
                    borderTop: 1,
                    borderColor: 'divider',
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {formatEastern(video.publishAt)}
                  </Typography>
                  <Link component={RouterLink} to={`/ai-stories/${video.storyId}`} variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                    {video.title}
                  </Link>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Chip size="small" label={stage.label} color={stage.color} />
                    {video.youtubeVideoId && (
                      <Link
                        href={`https://studio.youtube.com/video/${video.youtubeVideoId}/edit`}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="caption"
                      >
                        Studio
                      </Link>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={runNow}
          disabled={running || loading || !automation?.enabled}
          startIcon={running ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
        >
          {running ? 'Running…' : 'Run now'}
        </Button>
        <Button variant="contained" onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
